import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine, ZAxis
} from 'recharts';
import { getFiles, getPreview, predict, downloadCsv } from '../services/api';
import './Dashboard.css';

const PAGE_SIZE = 15;

const Dashboard = ({ user, onLogout }) => {
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [predicting, setPredicting] = useState(false);
  const [error, setError] = useState('');
  const [previewPage, setPreviewPage] = useState(0);
  const [resultPage, setResultPage] = useState(0);

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      const res = await getFiles();
      setFiles(res.data.files);
      if (res.data.files.length > 0) {
        setSelectedFile(res.data.files[0]);
      }
    } catch {
      setError('Failed to load files');
    }
  };

  const handleRefresh = useCallback(async () => {
    if (!selectedFile) return;
    setLoading(true);
    setError('');
    setPredictions(null);
    try {
      const res = await getPreview(selectedFile);
      setPreviewData(res.data);
      setPreviewPage(0);
    } catch {
      setError('Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [selectedFile]);

  useEffect(() => {
    if (selectedFile) {
      handleRefresh();
    }
  }, [selectedFile, handleRefresh]);

  const handlePredict = async () => {
    if (!selectedFile) return;
    setPredicting(true);
    setError('');
    try {
      const res = await predict(selectedFile);
      setPredictions(res.data);
      setResultPage(0);
    } catch (err) {
      const msg = err.response?.data?.error || 'Prediction failed';
      setError(msg);
    } finally {
      setPredicting(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedFile) return;
    try {
      const res = await downloadCsv(selectedFile);
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Predicted_${selectedFile}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Download failed');
    }
  };

  const formatNumber = (val) => {
    if (val === null || val === undefined) return '—';
    const num = Number(val);
    if (isNaN(num)) return val;
    if (Math.abs(num) > 1000) return num.toFixed(2);
    if (Math.abs(num) < 0.001) return num.toExponential(4);
    return num.toFixed(6);
  };

  const formatAxisTick = (val) => {
    if (val === null || val === undefined) return '';
    const num = Number(val);
    if (isNaN(num)) return val;
    const abs = Math.abs(num);
    if (abs >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (abs >= 1e3) return (num / 1e3).toFixed(1) + 'K';
    if (abs >= 1) return num.toFixed(2);
    return num.toFixed(3);
  };

  const renderTable = (columns, rows, page, setPage, label) => {
    const totalPages = Math.ceil(rows.length / PAGE_SIZE);
    const start = page * PAGE_SIZE;
    const pageRows = rows.slice(start, start + PAGE_SIZE);

    return (
      <div className="table-container">
        <div className="table-header-bar">
          <span className="table-label">{label}</span>
          <span className="table-info">
            Showing {start + 1}–{Math.min(start + PAGE_SIZE, rows.length)} of {rows.length} rows
          </span>
        </div>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                {columns.map((col, i) => (
                  <th key={i}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci}>{formatNumber(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</button>
            <span>Page {page + 1} of {totalPages}</span>
            <button disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>
    );
  };

  const buildChartData = (chartInfo) => {
    return chartInfo.depth.map((d, i) => ({
      depth: Number(d).toFixed(2),
      actual: chartInfo.actual[i],
      predicted: chartInfo.predicted[i]
    }));
  };

  const buildScatterData = (chartInfo) => {
    return chartInfo.actual.map((actual, i) => ({
      actual,
      predicted: chartInfo.predicted[i]
    }));
  };

  const getAxisRange = (scatterData) => {
    const values = scatterData.flatMap((d) => [d.actual, d.predicted]);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.05 || 1;
    return [min - padding, max + padding];
  };

  const renderChartHeader = (title, subtitle, r2) => (
    <div className="chart-header">
      <div className="chart-title-group">
        <h3 className="chart-title">{title}</h3>
        {subtitle && <span className="chart-subtitle">{subtitle}</span>}
      </div>
      {r2 !== undefined && r2 !== null && (
        <span className="r2-badge" title="Coefficient of determination">
          R² <strong>{Number(r2).toFixed(4)}</strong>
        </span>
      )}
    </div>
  );

  const tooltipStyle = {
    background: '#1a2240',
    border: '1px solid #2d3a55',
    borderRadius: 8,
    color: '#e2e8f0',
    fontSize: 12,
    padding: '8px 12px'
  };

  const scatterTooltipStyle = {
    background: 'rgba(15, 21, 40, 0.95)',
    border: '1px solid rgba(255, 255, 255, 0.35)',
    borderRadius: 8,
    color: '#ffffff',
    fontSize: 12,
    padding: '8px 12px'
  };

  const legendStyle = {
    color: '#e2e8f0',
    fontSize: 12,
    paddingBottom: 8
  };

  const renderDepthChart = (title, chartInfo, yLabel) => {
    const chartData = buildChartData(chartInfo);
    const tickInterval = Math.max(1, Math.floor(chartData.length / 6));
    return (
      <div className="chart-card">
        {renderChartHeader(title, 'Property profile across depth', chartInfo.r2)}
        <ResponsiveContainer width="100%" height={360}>
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 24, left: 24, bottom: 40 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#1e2a45" />
            <XAxis
              dataKey="depth"
              stroke="#5a6a85"
              tick={{ fill: '#8892a4', fontSize: 11 }}
              tickMargin={8}
              interval={tickInterval}
              label={{
                value: 'Depth (ft)',
                position: 'insideBottom',
                offset: -28,
                fill: '#a0b0d0',
                fontSize: 12
              }}
            />
            <YAxis
              stroke="#5a6a85"
              tick={{ fill: '#8892a4', fontSize: 11 }}
              tickFormatter={formatAxisTick}
              tickMargin={6}
              width={70}
              label={{
                value: yLabel,
                angle: -90,
                position: 'insideLeft',
                offset: 8,
                fill: '#a0b0d0',
                fontSize: 12,
                style: { textAnchor: 'middle' }
              }}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              labelStyle={{ color: '#c4952a', marginBottom: 4 }}
              labelFormatter={(label) => `Depth: ${label} ft`}
              formatter={(value, name) => [formatNumber(value), name]}
            />
            <Legend verticalAlign="top" align="right" height={28} wrapperStyle={legendStyle} />
            <Line
              type="monotone"
              dataKey="actual"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
              name="Actual"
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="predicted"
              stroke="#ef4444"
              strokeWidth={2}
              dot={false}
              name="Predicted"
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderScatterChart = (title, chartInfo, label) => {
    const scatterData = buildScatterData(chartInfo);
    const [axisMin, axisMax] = getAxisRange(scatterData);

    return (
      <div className="chart-card">
        {renderChartHeader(
          `${title} — Actual vs Predicted`,
          'Closer to the dashed line means better prediction',
          chartInfo.r2
        )}
        <ResponsiveContainer width="100%" height={360}>
          <ScatterChart margin={{ top: 10, right: 24, left: 24, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e2a45" />
            <XAxis
              type="number"
              dataKey="actual"
              name="Actual"
              domain={[axisMin, axisMax]}
              stroke="#5a6a85"
              tick={{ fill: '#8892a4', fontSize: 11 }}
              tickFormatter={formatAxisTick}
              tickMargin={8}
              label={{
                value: `Actual ${label}`,
                position: 'insideBottom',
                offset: -28,
                fill: '#a0b0d0',
                fontSize: 12
              }}
            />
            <YAxis
              type="number"
              dataKey="predicted"
              name="Predicted"
              domain={[axisMin, axisMax]}
              stroke="#5a6a85"
              tick={{ fill: '#8892a4', fontSize: 11 }}
              tickFormatter={formatAxisTick}
              tickMargin={6}
              width={70}
              label={{
                value: `Predicted ${label}`,
                angle: -90,
                position: 'insideLeft',
                offset: 8,
                fill: '#a0b0d0',
                fontSize: 12,
                style: { textAnchor: 'middle' }
              }}
            />
            <ZAxis range={[36, 36]} />
            <Tooltip
              cursor={{ strokeDasharray: '3 3', stroke: '#ffffff' }}
              contentStyle={scatterTooltipStyle}
              labelStyle={{ color: '#ffffff', marginBottom: 4 }}
              itemStyle={{ color: '#ffffff' }}
              wrapperClassName="scatter-chart-tooltip"
              formatter={(value, name) => [formatNumber(value), name]}
            />
            <ReferenceLine
              segment={[
                { x: axisMin, y: axisMin },
                { x: axisMax, y: axisMax }
              ]}
              stroke="#f59e0b"
              strokeDasharray="6 4"
              ifOverflow="extendDomain"
            />
            <Scatter
              data={scatterData}
              fill="#3b82f6"
              fillOpacity={0.55}
              stroke="#60a5fa"
              strokeWidth={0.5}
              legendType="none"
              activeDot={{ r: 6, fill: '#ffffff', stroke: '#ffffff', strokeWidth: 2 }}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderErrorTable = (errorInfo) => {
    const rows = errorInfo.data.map((row, i) => [
      errorInfo.depths[i],
      ...row
    ]);
    return renderTable(errorInfo.columns, rows, resultPage, setResultPage, '');
  };

  return (
    <div className="dashboard">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-top">
          <div className="brand">
            <img src="/logo.jpg" alt="Geo-Mechanical" className="brand-logo" />
          </div>

          <div className="sidebar-section">
            <label className="sidebar-label">SELECT TESTING CSV</label>
            <select
              className="sidebar-select"
              value={selectedFile}
              onChange={(e) => setSelectedFile(e.target.value)}
            >
              {files.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>

          <button className="sidebar-btn btn-refresh" onClick={handleRefresh} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh Preview'}
          </button>

          <button className="sidebar-btn btn-predict" onClick={handlePredict} disabled={predicting}>
            {predicting ? 'Predicting...' : 'Predict'}
          </button>

          {predictions && (
            <button className="sidebar-btn btn-download" onClick={handleDownload}>
              Download Predicted CSV
            </button>
          )}
        </div>

        <div className="sidebar-bottom">
          <div className="user-badge">{user}</div>
          <button className="sidebar-btn btn-logout" onClick={onLogout}>
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <h1 className="page-title">Geo-Mechanical Properties Prediction</h1>

        {error && <div className="error-banner">{error}</div>}

        {/* Data Preview */}
        {previewData && (
          <>
            <h2 className="section-title">Data Preview</h2>
            {renderTable(previewData.columns, previewData.data, previewPage, setPreviewPage, '')}
          </>
        )}

        {/* Prediction Summary */}
        {predictions && (
          <>
            <div className="success-banner">Predictions Generated Successfully!</div>

            <h2 className="section-title">Data with Predictions</h2>
            {renderTable(predictions.columns, predictions.data, resultPage, setResultPage, '')}

            {/* Poisson Ratio Section */}
            <h2 className="section-title">Poisson Ratio Analysis</h2>
            <div className="charts-row">
              {renderDepthChart(
                'Poisson Ratio vs Depth',
                predictions.charts.poisson,
                "Dynamic Poisson's Ratio"
              )}
              {renderScatterChart(
                'Poisson Ratio',
                predictions.charts.poisson,
                "Poisson's Ratio"
              )}
            </div>
            <div className="error-table-wrap full-width">
              <h3 className="chart-title">Error Table — Poisson Ratio</h3>
              {renderErrorTable(predictions.error_tables.poisson)}
            </div>

            {/* Young's Modulus Section */}
            <h2 className="section-title">Young's Modulus Analysis</h2>
            <div className="charts-row">
              {renderDepthChart(
                "Young's Modulus vs Depth",
                predictions.charts.youngs,
                "Young's Modulus"
              )}
              {renderScatterChart(
                "Young's Modulus",
                predictions.charts.youngs,
                "Young's Modulus"
              )}
            </div>
            <div className="error-table-wrap full-width">
              <h3 className="chart-title">Error Table — Young's Modulus</h3>
              {renderErrorTable(predictions.error_tables.youngs)}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
